import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  useCommunityEngagement,
  useCommunityFeed,
  useCommunityMediaUpload,
  useCommunityPostSeenList,
  useRecordCommunityPostsSeen,
  useCreateCommunityPost,
  useEditCommunityPost,
  useCommunityRealtime,
  useSetCommunityPostPinned,
  useToggleCommunityPostLike,
} from '../hooks/useCommunity';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useEvents, type Event } from '../hooks/useEvents';
import { hasAdminRole, hasAnyRole, hasPermission, Permission } from '../utils/roles';
import { SeenByDialog } from '../components/SeenByDialog';
import PostRichContent from '../components/social/PostRichContent';
import OriginalPostDialog from '../components/social/OriginalPostDialog';
import { canEditWallPost, isPostEdited, readOriginalPost, type OriginalPostSnapshot } from '../utils/postEdit';
import {
  checkVideoUploadSize,
  MAX_FEED_IMAGE_UPLOAD_BYTES,
  formatBytesAsMb,
} from '../utils/mediaUploadLimits';
import { uploadVideoToBunny } from '../services/bunnyVideoService';

const Page = styled.div`
  min-height: 100%;
  background:
    radial-gradient(ellipse 120% 80% at 50% -20%, color-mix(in srgb, var(--primary) 28%, transparent) 0%, transparent 55%),
    radial-gradient(ellipse 60% 40% at 100% 40%, color-mix(in srgb, var(--primary) 8%, transparent) 0%, transparent 50%),
    var(--background);
  padding: clamp(1rem, 2.5vw, 1.75rem);
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) min(100%, 320px);
  gap: clamp(1rem, 2vw, 1.35rem);
  min-height: calc(100vh - 3.5rem);
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) min(100%, 300px);
  }

  @media (max-width: 930px) {
    grid-template-columns: 1fr;
  }
`;

const FrostCard = styled.section`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  border-radius: 22px;
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 94%, transparent) 0%,
    color-mix(in srgb, var(--surface) 88%, transparent) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 1px 0 color-mix(in srgb, var(--onSurface) 6%, transparent),
    0 12px 40px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.06);
`;

const PageIntro = styled.header`
  margin-bottom: 0.15rem;
`;

const IntroTitle = styled.h1`
  margin: 0;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.35rem, 2.8vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--onSurface);
  line-height: 1.2;
`;

const IntroSubtitle = styled.p`
  margin: 0.45rem 0 0;
  max-width: 36rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--onSurfaceVariant);
`;

const FeedControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const FeedControlsCard = styled(FrostCard)`
  padding: 0.9rem 1rem;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 35%, transparent)'
        : 'color-mix(in srgb, var(--outline) 78%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 14%, var(--surface)), color-mix(in srgb, var(--primary) 8%, transparent))'
      : 'color-mix(in srgb, var(--surface) 86%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  border-radius: 999px;
  padding: 0.5rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;


const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: clamp(0.9rem, 1.5vw, 1.15rem);
  min-width: 0;
`;

const ComposerLauncher = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.25rem);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ComposerLauncherRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;

  @media (max-width: 680px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ComposerPromptButton = styled.button`
  flex: 1;
  min-height: 54px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 55%, var(--surface));
  color: var(--onSurfaceVariant);
  padding: 0.9rem 1rem;
  font-size: 0.95rem;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
    background: color-mix(in srgb, var(--surface) 96%, var(--surfaceVariant, var(--surface)));
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const ComposerLauncherHint = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  line-height: 1.45;
`;

const ComposerModalScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: color-mix(in srgb, var(--scrim, #09131f) 64%, transparent);
`;

const Composer = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.25rem);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ComposerModalCard = styled(Composer)`
  width: min(100%, 720px);
  max-height: min(90vh, 760px);
  overflow: auto;
`;

const ComposerModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const ComposerModalTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
`;

const ComposerModalTitle = styled.h2`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const ComposerModalSubtitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  line-height: 1.45;
`;

const ComposerCloseButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 75%, transparent);
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--onSurface);
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const ComposerInput = styled.textarea`
  resize: vertical;
  min-height: 92px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 90%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 55%, var(--surface));
  color: var(--onSurface);
  padding: 0.95rem 1rem;
  font-size: 0.95rem;
  line-height: 1.45;
  font-family: inherit;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &::placeholder {
    color: color-mix(in srgb, var(--onSurfaceVariant) 72%, transparent);
  }

  &:hover:not(:disabled) {
    border-color: color-mix(in srgb, var(--onSurfaceVariant) 22%, var(--outline));
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    background: color-mix(in srgb, var(--surface) 96%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const ComposerActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
`;

const ComposerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const ProfileLink = styled(Link)`
  text-decoration: none;
  color: var(--onSurfaceVariant);
  font-weight: 600;
  font-size: 0.83rem;
  padding: 0.5rem 0.95rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  background: color-mix(in srgb, var(--surface) 82%, transparent);
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;

  &:hover {
    color: var(--onSurface);
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
    background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const SubtleActionRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  background: color-mix(in srgb, var(--surface) 40%, transparent);
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    color: var(--onSurface);
    border-color: color-mix(in srgb, var(--onSurfaceVariant) 18%, var(--outline));
    background: color-mix(in srgb, var(--surface) 70%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 92%, white) 0%,
    var(--primary) 45%,
    var(--primaryDark, var(--primary)) 100%
  );
  color: var(--onPrimary);
  border-radius: 12px;
  padding: 0.52rem 1.15rem;
  font-size: 0.84rem;
  font-weight: 700;
  font-family: inherit;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 35%, transparent);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px color-mix(in srgb, var(--primary) 40%, transparent);
    filter: brightness(1.03);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    filter: grayscale(0.15);
    opacity: 0.7;
    transform: none;
    box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const ComposerMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const ComposerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.42rem 0.72rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--primary);
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--primary) 22%, transparent);
`;

const ComposerStatus = styled.p<{ $tone?: 'default' | 'error' | 'success' }>`
  margin: 0;
  font-size: 0.78rem;
  color: ${({ $tone }) =>
    $tone === 'error'
      ? 'var(--error, #b42318)'
      : $tone === 'success'
      ? 'var(--primary)'
      : 'var(--onSurfaceVariant)'};
`;

const ComposerAttachment = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const AttachmentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.44rem 0.72rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 70%, transparent);
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
  color: var(--onSurfaceVariant);
  font-size: 0.74rem;
  font-weight: 600;
`;

const AttachmentClearButton = styled.button`
  border: none;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
`;

const ScheduleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
`;

const ScheduleInput = styled.input`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  font: inherit;
  font-size: 0.82rem;
`;

const WallFeed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WallItemCard = styled(FrostCard)<{ $clickable?: boolean }>`
  padding: 1.05rem 1.1rem;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--primary) 22%, var(--outline));
    box-shadow:
      0 1px 0 color-mix(in srgb, var(--onSurface) 6%, transparent),
      0 16px 48px rgba(0, 0, 0, 0.14),
      0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 3px;
  }
`;

const ItemTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
`;

const FeedBadge = styled.span<{ $kind: 'post' | 'prayer' | 'testimony' | 'occasion' }>`
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ $kind }) => ($kind === 'prayer' ? 'var(--onPrimary)' : 'var(--onSurface)')};
  background: ${({ $kind }) =>
    $kind === 'prayer'
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 88%, white), var(--primary))'
      : $kind === 'testimony'
      ? 'color-mix(in srgb, var(--primary) 18%, var(--surface))'
      : $kind === 'occasion'
      ? 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 90%, transparent)'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 55%, transparent)'};
  border: 1px solid
    ${({ $kind }) =>
      $kind === 'prayer'
        ? 'transparent'
        : $kind === 'testimony'
        ? 'color-mix(in srgb, var(--primary) 35%, transparent)'
        : 'color-mix(in srgb, var(--outline) 80%, transparent)'};
  box-shadow: ${({ $kind }) =>
    $kind === 'prayer' ? '0 2px 8px color-mix(in srgb, var(--primary) 30%, transparent)' : 'none'};
`;

const ItemTimestamp = styled.span`
  font-size: 0.74rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  font-variant-numeric: tabular-nums;
`;

const ItemTitle = styled.p`
  margin: 0.7rem 0 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.3;
`;

const ItemAuthor = styled.p`
  margin: 0.28rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const ItemBody = styled.p`
  margin: 0.6rem 0 0;
  color: color-mix(in srgb, var(--onSurfaceVariant) 92%, var(--onSurface));
  font-size: 0.875rem;
  line-height: 1.55;
`;

const ItemFooter = styled.div`
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 65%, transparent);
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const ItemStat = styled.span`
  font-size: 0.76rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
`;

const ItemActionRow = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const ItemActionButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 35%, transparent)'
        : 'color-mix(in srgb, var(--outline) 75%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 10%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 82%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.38rem 0.64rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--onSurface);
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
    background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const PaginationBar = styled(FrostCard)`
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const PaginationStatus = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  font-weight: 600;
`;

const PaginationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const PaginationButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  background: color-mix(in srgb, var(--surface) 86%, transparent);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.48rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
    background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const RightRail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 1200px) {
    gap: 0.85rem;
  }
`;

const RailCard = styled(FrostCard)`
  padding: 1.05rem 1.1rem;
`;

const RailLabel = styled.p`
  margin: 0 0 0.85rem;
  color: var(--onSurfaceVariant);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 0.65rem;
  font-weight: 700;
  font-family: 'Poppins', system-ui, sans-serif;
`;

const RailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RailActionButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-radius: 14px;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 50%, transparent);
  padding: 0.72rem 0.85rem;
  font-size: 0.87rem;
  font-weight: 600;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
    background: color-mix(in srgb, var(--primary) 7%, var(--surface));
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const EventItem = styled.div`
  border-bottom: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  padding-bottom: 0.7rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const EventTitle = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.35;
`;

const EventMeta = styled.p`
  margin: 0.28rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
  line-height: 1.4;
`;

type DashboardListItem = {
  id?: string;
  title?: string;
  content?: string;
  author_name?: string;
  post_likes?: { count: number }[];
  wall_comments?: { count: number }[];
  likesCount?: number;
  commentsCount?: number;
  seenCount?: number | null;
  speaker?: string;
  created_at?: string | null;
  type?: string | null;
  thumbnail_url?: string | null;
  image_url?: string | null;
  video_link?: string | null;
  video_url?: string | null;
  images?: Array<string | null> | null;
  location?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  is_pinned?: boolean | null;
  edited_at?: string | null;
  original_post?: Record<string, unknown> | null;
  author_id?: string | null;
  poster_id?: string | null;
  video_id?: string | null;
  video_processing_status?: string | null;
};

const getEventStartDateTime = (event: Pick<Event, 'start_date' | 'start_time'>) => {
  const startDate = typeof event.start_date === 'string' ? event.start_date.trim() : '';
  if (!startDate) return null;

  const hasExplicitDateTime = startDate.includes('T');
  const fallbackTime = '23:59:59';
  const rawDateTime = hasExplicitDateTime
    ? startDate
    : `${startDate}T${(event.start_time || fallbackTime).trim() || fallbackTime}`;
  const parsed = new Date(rawDateTime);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  const fallback = new Date(startDate);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatEventMeta = (dateValue?: string, timeValue?: string, location?: string) => {
  const startsAt = getEventStartDateTime({ start_date: dateValue ?? '', start_time: timeValue ?? '' });
  const dateTimeLabel = startsAt
    ? startsAt.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Date TBD';
  return location ? `${dateTimeLabel} - ${location}` : dateTimeLabel;
};

const formatShortDate = (value?: string | null) => {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

type FeedEntry = {
  key: string;
  kind: 'post' | 'prayer' | 'testimony' | 'occasion';
  badge: string;
  title: string;
  body: string;
  author: string;
  images?: string[];
  video?: string;
  dateValue?: string | null;
  dateLabel: string;
  likes?: number;
  comments?: number;
  seenCount?: number | null;
  postId?: string;
  isPinned?: boolean;
  edited?: boolean;
  originalPost?: OriginalPostSnapshot | null;
  canEdit?: boolean;
  sourcePost?: DashboardListItem;
};

type CommunityTabKey = 'feed' | 'prayer' | 'testimonies';
type CommunityFilterKey = 'latest' | 'media';

const FEED_PAGE_SIZE = 12;

const COMMUNITY_TABS: Array<{
  key: CommunityTabKey;
  label: string;
  subtitle: string;
  types?: string[];
}> = [
  {
    key: 'feed',
    label: 'Feed',
    subtitle: 'The latest wall posts, prayer requests, and testimonies from the community.',
  },
  {
    key: 'prayer',
    label: 'Prayer',
    subtitle: 'Focus the wall on prayer requests and follow the latest needs first.',
    types: ['prayer'],
  },
  {
    key: 'testimonies',
    label: 'Testimonies',
    subtitle: 'Celebrate praise reports and testimonies without the rest of the wall noise.',
    types: ['testimony', 'praise_report'],
  },
];

const parseCommunityTab = (value: string | null): CommunityTabKey => {
  if (value === 'prayer' || value === 'testimonies') return value;
  return 'feed';
};

const parseCommunityFilter = (value: string | null): CommunityFilterKey => {
  return value === 'media' ? 'media' : 'latest';
};

const parseCommunityPage = (value: string | null): number => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getPostKind = (type?: string | null): FeedEntry['kind'] => {
  if (type === 'prayer') return 'prayer';
  if (type === 'testimony' || type === 'praise_report') return 'testimony';
  return 'post';
};

const getBadgeLabel = (kind: FeedEntry['kind']) => {
  if (kind === 'prayer') return 'Prayer';
  if (kind === 'testimony') return 'Testimony';
  if (kind === 'occasion') return 'Occasion';
  return 'Wall Post';
};

export const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canViewSeenStatus = hasAdminRole(roles);
  const canPinWall = hasAnyRole(roles, ['admin', 'super_admin']);
  const canViewMessages = hasPermission(profile?.roles, Permission.VIEW_USERS);
  const activeTab = parseCommunityTab(searchParams.get('tab'));
  const activeFilter = parseCommunityFilter(searchParams.get('filter'));
  const activePage = parseCommunityPage(searchParams.get('page'));
  const activeTabConfig = COMMUNITY_TABS.find((tab) => tab.key === activeTab) ?? COMMUNITY_TABS[0];
  const [composerValue, setComposerValue] = useState('');
  const [composerType, setComposerType] = useState<'post' | 'prayer' | 'testimony' | 'praise_report'>('post');
  const [imageUrl, setImageUrl] = useState('');
  const [keptImageUrls, setKeptImageUrls] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [videoProcessingStatus, setVideoProcessingStatus] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [composerTone, setComposerTone] = useState<'default' | 'error' | 'success'>('default');
  const [composerMessage, setComposerMessage] = useState<string | null>(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  useCommunityRealtime();
  const { data: feedData, isLoading: postsLoading, error: postsError } = useCommunityFeed({
    page: activePage,
    limit: FEED_PAGE_SIZE,
    mediaOnly: activeFilter === 'media',
    types: activeTabConfig.types,
  });
  const { data: events } = useEvents(true);
  const socialPosts = useMemo(
    () => (Array.isArray(feedData) ? feedData : feedData?.posts ?? []),
    [feedData]
  );
  const postIds = useMemo(() => socialPosts.map((post) => post.id), [socialPosts]);
  const { data: engagement } = useCommunityEngagement(user?.id, postIds);
  const recordSeen = useRecordCommunityPostsSeen();
  const createPost = useCreateCommunityPost(user, profile?.full_name);
  const editPost = useEditCommunityPost();
  const toggleLike = useToggleCommunityPostLike(user?.id);
  const setPostPinned = useSetCommunityPostPinned();
  const uploadMedia = useCommunityMediaUpload();
  const [activeSeenPostId, setActiveSeenPostId] = useState<string | null>(null);
  const [originalSnapshot, setOriginalSnapshot] = useState<OriginalPostSnapshot | null>(null);
  const feedCardRefs = useRef<Record<string, HTMLElement | null>>({});
  const seenPostIdsRef = useRef<Set<string>>(new Set());
  const { data: seenList, isLoading: seenListLoading } = useCommunityPostSeenList(
    activeSeenPostId ?? undefined,
    canViewSeenStatus && Boolean(activeSeenPostId)
  );

  const totalFeedPosts = Array.isArray(feedData) ? feedData.length : feedData?.totalCount ?? socialPosts.length;
  const totalPages = Math.max(1, Math.ceil(totalFeedPosts / FEED_PAGE_SIZE));
  const likedPostIds = engagement?.likedPostIds ?? [];
  const commentedPostIds = engagement?.commentedPostIds ?? [];
  const postEntries: FeedEntry[] = socialPosts.map((item) => {
    const typedItem = item as DashboardListItem;
    const kind = getPostKind(typedItem.type);
    return {
      key: `post-${typedItem.id ?? Math.random().toString(36).slice(2)}`,
      kind,
      badge: getBadgeLabel(kind),
      title: typedItem.title || (kind === 'prayer' ? 'Prayer Request' : 'Community Share'),
      body: typedItem.content || 'No details shared yet.',
      author: typedItem.author_name || 'Community Member',
      images: [
        ...(Array.isArray(typedItem.images) ? typedItem.images : []),
        ...(typedItem.image_url ? [typedItem.image_url] : []),
      ].filter((image): image is string => typeof image === 'string' && image.trim().length > 0),
      video: typedItem.video_url ?? typedItem.video_link ?? undefined,
      dateValue: typedItem.created_at,
      dateLabel: formatShortDate(typedItem.created_at),
      likes: typedItem.likesCount ?? typedItem.post_likes?.[0]?.count ?? 0,
      comments: typedItem.commentsCount ?? typedItem.wall_comments?.[0]?.count ?? 0,
      seenCount: typedItem.seenCount ?? null,
      postId: typedItem.id,
      isPinned: Boolean(typedItem.is_pinned),
      edited: isPostEdited(typedItem),
      originalPost: readOriginalPost(typedItem),
      canEdit: canEditWallPost(typedItem, user?.id),
      sourcePost: typedItem,
    };
  });
  // Preserve hook ordering: pinned section first, then chronological (do not re-sort pins away).
  const wallFeedEntries = postEntries;
  const upcomingEvents = useMemo(
    () =>
      (events ?? [])
        .filter((event) => {
          const startsAt = getEventStartDateTime(event);
          return Boolean(startsAt && startsAt.getTime() >= Date.now());
        })
        .sort((a, b) => {
          const aTime = getEventStartDateTime(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          const bTime = getEventStartDateTime(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          return aTime - bTime;
        })
        .slice(0, 3),
    [events]
  );
  const sidebarQuickActions = useMemo(
    () => [
      { label: 'Groups', path: '/groups' },
      ...(canViewMessages ? [{ label: 'Messages', path: '/admin/users/messages' }] : []),
      { label: 'Events', path: '/events' },
      { label: 'Service Planner', path: '/church-planner/dashboard' },
      { label: 'Facilities', path: '/facilities' },
    ],
    [canViewMessages]
  );

  useEffect(() => {
    if (!isComposerOpen) return undefined;

    const handle = window.requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(handle);
  }, [isComposerOpen]);

  useEffect(() => {
    if (!user?.id || wallFeedEntries.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visiblePostIds = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)
          .map((entry) => (entry.target as HTMLElement).dataset.postId ?? '')
          .filter((postId) => postId.length > 0)
          .filter((postId) => !seenPostIdsRef.current.has(postId));

        if (visiblePostIds.length === 0) return;

        visiblePostIds.forEach((postId) => seenPostIdsRef.current.add(postId));
        recordSeen.mutate(visiblePostIds);
      },
      { threshold: [0.6] }
    );

    wallFeedEntries.forEach((item) => {
      if (!item.postId) return;
      const node = feedCardRefs.current[item.postId];
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [recordSeen, user?.id, wallFeedEntries]);

  const activeSeenEntry = wallFeedEntries.find((entry) => entry.postId === activeSeenPostId) ?? null;

  const clearEditingState = () => {
    setEditingPostId(null);
    setKeptImageUrls([]);
    setVideoProcessingStatus(null);
  };

  const openComposer = (type: 'post' | 'prayer' | 'testimony' | 'praise_report' = 'post') => {
    resetComposerStatus();
    if (editingPostId) {
      setComposerValue('');
      setImageUrl('');
      setVideoUrl('');
      setVideoId('');
      clearEditingState();
    }
    setComposerType(type);
    setIsComposerOpen(true);
  };

  const focusComposer = () => {
    if (!isComposerOpen) {
      setIsComposerOpen(true);
      return;
    }

    composerInputRef.current?.focus();
  };

  const closeComposer = () => {
    setIsComposerOpen(false);
    clearEditingState();
  };

  const resetComposerStatus = useCallback(() => {
    setComposerTone('default');
    setComposerMessage(null);
  }, []);

  const beginEdit = useCallback((post: DashboardListItem) => {
    const images = (Array.isArray(post.images) ? post.images : []).filter(
      (image): image is string => typeof image === 'string' && image.trim().length > 0
    );
    resetComposerStatus();
    setEditingPostId(post.id ?? null);
    setComposerValue(post.content ?? '');
    setComposerType(
      post.type === 'prayer' || post.type === 'testimony' || post.type === 'praise_report' ? post.type : 'post'
    );
    setImageUrl(images[0] ?? '');
    setKeptImageUrls(images.slice(1));
    setVideoUrl(post.video_url ?? post.video_link ?? '');
    setVideoId(post.video_id ?? '');
    setVideoProcessingStatus(post.video_processing_status ?? (post.video_id ? 'processing' : 'ready'));
    setIsScheduled(false);
    setScheduledFor('');
    setIsComposerOpen(true);
  }, [resetComposerStatus]);

  useEffect(() => {
    const editingCommunityPost = (location.state as { editingCommunityPost?: DashboardListItem } | null)?.editingCommunityPost;
    if (!editingCommunityPost?.id) return;
    beginEdit(editingCommunityPost);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [beginEdit, location.pathname, location.search, location.state, navigate]);

  const handleComposerType = (type: 'post' | 'prayer' | 'testimony' | 'praise_report') => {
    resetComposerStatus();
    setComposerType(type);
    setIsComposerOpen(true);
  };

  const handleFileUpload = async (file: File | undefined, kind: 'image' | 'video') => {
    if (!file) return;

    if (kind === 'image' && file.size > MAX_FEED_IMAGE_UPLOAD_BYTES) {
      setComposerTone('error');
      setComposerMessage(
        `Images must be smaller than ${formatBytesAsMb(MAX_FEED_IMAGE_UPLOAD_BYTES)}MB.`
      );
      return;
    }

    if (kind === 'video') {
      const sizeCheck = checkVideoUploadSize(file.size);
      if (!sizeCheck.ok) {
        setComposerTone('error');
        setComposerMessage(sizeCheck.reason);
        return;
      }
      if (!user?.id) {
        setComposerTone('error');
        setComposerMessage('You must be signed in to upload a video.');
        return;
      }
    }

    try {
      setComposerTone('default');
      setComposerMessage(kind === 'image' ? 'Uploading image...' : 'Uploading video to Bunny...');
      if (kind === 'image') {
        const url = await uploadMedia.mutateAsync(file);
        if (editingPostId && imageUrl) {
          setKeptImageUrls((current) => [...current, url]);
        } else {
          setImageUrl(url);
        }
        setComposerTone('success');
        setComposerMessage('Image attached.');
      } else {
        const uploadResult = await uploadVideoToBunny(
          file,
          {
            title: composerValue.trim() || file.name || 'Community video',
            description: composerValue.trim() || 'Video uploaded with community post',
            category: 'Community',
          },
          user!.id
        );
        if (!uploadResult.success || !uploadResult.data) {
          throw new Error(uploadResult.error || 'Failed to upload video to Bunny.');
        }
        setVideoUrl(uploadResult.data.videoUrl);
        setVideoId(uploadResult.data.videoId);
        setVideoProcessingStatus('processing');
        setComposerTone('success');
        setComposerMessage('Video attached. Processing will finish in the background.');
      }
      focusComposer();
    } catch (error) {
      setComposerTone('error');
      setComposerMessage(error instanceof Error ? error.message : 'Upload failed. Please try again.');
    } finally {
      if (kind === 'image' && imageInputRef.current) imageInputRef.current.value = '';
      if (kind === 'video' && videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleShare = async () => {
    resetComposerStatus();

    if (isScheduled && (!scheduledFor || new Date(scheduledFor).getTime() <= Date.now())) {
      setComposerTone('error');
      setComposerMessage('Please choose a date and time in the future.');
      return;
    }

    try {
      if (editingPostId) {
        const images = [imageUrl, ...keptImageUrls].map((url) => url.trim()).filter(Boolean);
        await editPost.mutateAsync({
          postId: editingPostId,
          content: composerValue,
          images,
          videoUrl: videoUrl || null,
          videoId: videoId || null,
          videoProcessingStatus,
        });
        setComposerValue('');
        setComposerType('post');
        setImageUrl('');
        setVideoUrl('');
        setVideoId('');
        clearEditingState();
        setComposerTone('success');
        setComposerMessage('Your post was updated.');
        setIsComposerOpen(false);
        return;
      }

      const scheduledForIso = isScheduled && scheduledFor ? new Date(scheduledFor).toISOString() : undefined;
      await createPost.mutateAsync({
        content: composerValue,
        type: composerType,
        imageUrl: imageUrl || undefined,
        videoUrl: videoUrl || undefined,
        videoId: videoId || undefined,
        scheduledFor: scheduledForIso,
      });
      setComposerValue('');
      setComposerType('post');
      setImageUrl('');
      setVideoUrl('');
      setVideoId('');
      setComposerTone('success');
      setComposerMessage(
        scheduledForIso
          ? `Your post is scheduled for ${new Date(scheduledForIso).toLocaleString()}.`
          : 'Your update is live on the community wall.'
      );
      setIsScheduled(false);
      setScheduledFor('');
    } catch (error) {
      setComposerTone('error');
      setComposerMessage(error instanceof Error ? error.message : 'Unable to publish your post.');
    }
  };

  const handleToggleLike = async (postId?: string) => {
    if (!postId) return;

    try {
      await toggleLike.mutateAsync(postId);
    } catch (error) {
      setComposerTone('error');
      setComposerMessage(error instanceof Error ? error.message : 'Unable to update your like right now.');
    }
  };

  const handleTogglePinned = async (postId?: string, isPinned?: boolean) => {
    if (!postId || !canPinWall) return;

    try {
      await setPostPinned.mutateAsync({ postId, pinned: !Boolean(isPinned) });
    } catch (error) {
      setComposerTone('error');
      setComposerMessage(error instanceof Error ? error.message : 'Unable to update pin right now.');
    }
  };

  const updateSearch = (updates: Partial<Record<'tab' | 'filter' | 'page', string | null>>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const openPost = (postId?: string) => {
    if (!postId) return;
    const suffix = activeTab === 'feed' && activeFilter === 'latest'
      ? ''
      : `?${new URLSearchParams({
          ...(activeTab !== 'feed' ? { tab: activeTab } : {}),
          ...(activeFilter !== 'latest' ? { filter: activeFilter } : {}),
          ...(activePage > 1 ? { page: String(activePage) } : {}),
        }).toString()}`;
    navigate(`/community/posts/${postId}${suffix}`);
  };

  const handleFeedCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, postId?: string) => {
    if (!postId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPost(postId);
    }
  };

  const feedErrorMessage = postsError instanceof Error ? postsError.message : 'Unable to load the community feed.';

  return (
    <Page>
      <Shell>
        <Main>
          <PageIntro>
            <IntroTitle>Community wall</IntroTitle>
            <IntroSubtitle>
              {activeTabConfig.subtitle}
            </IntroSubtitle>
          </PageIntro>

          <ComposerLauncher>
           
            <ComposerLauncherRow>
              <ComposerPromptButton type="button" onClick={() => openComposer('post')}>
                What's on your heart?
              </ComposerPromptButton>
              <PrimaryButton type="button" onClick={() => openComposer('post')}>
                Create post
              </PrimaryButton>
            </ComposerLauncherRow>
            <ComposerLauncherHint>
              Open a focused composer to post an update, ask for prayer, share a testimony, or attach media.
            </ComposerLauncherHint>
          </ComposerLauncher>

          <FeedControlsCard>
            <FeedControls>
              <TabRow>
                {COMMUNITY_TABS.map((tab) => (
                  <TabButton
                    key={tab.key}
                    type="button"
                    $active={tab.key === activeTab}
                    onClick={() =>
                      updateSearch({
                        tab: tab.key === 'feed' ? null : tab.key,
                        page: null,
                      })
                    }
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </TabRow>

            </FeedControls>
          </FeedControlsCard>

          {isComposerOpen ? (
            <ComposerModalScrim onClick={closeComposer}>
              <ComposerModalCard
                role="dialog"
                aria-modal="true"
                aria-labelledby="community-share-dialog-title"
                onClick={(event) => event.stopPropagation()}
              >
                <ComposerModalHeader>
                  <ComposerModalTitleWrap>
                    <ComposerModalTitle id="community-share-dialog-title">
                      {editingPostId ? 'Edit post' : 'Create a post'}
                    </ComposerModalTitle>
                    <ComposerModalSubtitle>
                      {editingPostId
                        ? 'Update your post. The original version stays available from Edited.'
                        : 'Share something with the wall without leaving the feed.'}
                    </ComposerModalSubtitle>
                  </ComposerModalTitleWrap>
                  <ComposerCloseButton type="button" aria-label="Close share dialog" onClick={closeComposer}>
                    ×
                  </ComposerCloseButton>
                </ComposerModalHeader>

                <ComposerMeta>
                  <ComposerBadge>
                    {composerType === 'prayer'
                      ? 'Prayer request'
                      : composerType === 'testimony'
                      ? 'Testimony'
                      : composerType === 'praise_report'
                      ? 'Praise report'
                      : 'General post'}
                  </ComposerBadge>
                  {composerMessage ? <ComposerStatus $tone={composerTone}>{composerMessage}</ComposerStatus> : null}
                </ComposerMeta>
                <ComposerInput
                  ref={composerInputRef}
                  placeholder="What's on your heart?"
                  value={composerValue}
                  onChange={(event) => {
                    setComposerValue(event.target.value);
                    if (composerTone !== 'default') resetComposerStatus();
                  }}
                />
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => handleFileUpload(event.target.files?.[0], 'image')}
                />
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={(event) => handleFileUpload(event.target.files?.[0], 'video')}
                />
                {(imageUrl || videoUrl || keptImageUrls.length > 0) && (
                  <ComposerAttachment>
                    {imageUrl ? (
                      <AttachmentChip>
                        Image attached
                        <AttachmentClearButton type="button" onClick={() => setImageUrl('')}>
                          Remove
                        </AttachmentClearButton>
                      </AttachmentChip>
                    ) : null}
                    {videoUrl ? (
                      <AttachmentChip>
                        Video attached
                        <AttachmentClearButton
                          type="button"
                          onClick={() => {
                            setVideoUrl('');
                            setVideoId('');
                            setVideoProcessingStatus(null);
                          }}
                        >
                          Remove
                        </AttachmentClearButton>
                      </AttachmentChip>
                    ) : null}
                    {keptImageUrls.map((url) => (
                      <AttachmentChip key={url}>
                        Extra image
                        <AttachmentClearButton
                          type="button"
                          onClick={() => setKeptImageUrls((current) => current.filter((item) => item !== url))}
                        >
                          Remove
                        </AttachmentClearButton>
                      </AttachmentChip>
                    ))}
                  </ComposerAttachment>
                )}
                {editingPostId ? null : (
                <ScheduleRow>
                  <input
                    type="checkbox"
                    checked={isScheduled}
                    onChange={(event) => setIsScheduled(event.target.checked)}
                  />
                  Schedule for later
                  {isScheduled ? (
                    <ScheduleInput
                      type="datetime-local"
                      value={scheduledFor}
                      min={new Date().toISOString().slice(0, 16)}
                      onChange={(event) => setScheduledFor(event.target.value)}
                    />
                  ) : null}
                </ScheduleRow>
                )}
                <ComposerActions>
                  <SubtleActionRow>
                    <GhostButton type="button" onClick={() => imageInputRef.current?.click()}>
                      Photo
                    </GhostButton>
                    <GhostButton type="button" onClick={() => videoInputRef.current?.click()}>
                      Video
                    </GhostButton>
                    {editingPostId ? null : (
                      <>
                    <GhostButton type="button" onClick={() => handleComposerType('prayer')}>
                      Prayer
                    </GhostButton>
                    <GhostButton type="button" onClick={() => handleComposerType('testimony')}>
                      Testimony
                    </GhostButton>
                      </>
                    )}
                  </SubtleActionRow>
                  <ComposerRight>
                    <ProfileLink to="/scheduled-posts">Scheduled Posts</ProfileLink>
                    <ProfileLink to="/profile">My Profile</ProfileLink>
                    <PrimaryButton
                      type="button"
                      onClick={handleShare}
                      disabled={createPost.isPending || editPost.isPending || uploadMedia.isPending}
                    >
                      {editingPostId
                        ? (editPost.isPending ? 'Saving...' : 'Save changes')
                        : createPost.isPending
                          ? 'Sharing...'
                          : isScheduled
                            ? 'Schedule'
                            : 'Share'}
                    </PrimaryButton>
                  </ComposerRight>
                </ComposerActions>
              </ComposerModalCard>
            </ComposerModalScrim>
          ) : null}

          <WallFeed>
            {postsLoading && (
              <WallItemCard>
                <ItemTitle>Loading community activity</ItemTitle>
                <ItemBody>Pulling the latest posts, prayers, and testimonies into the dashboard.</ItemBody>
              </WallItemCard>
            )}
            {!postsLoading && postsError && (
              <WallItemCard>
                <ItemTitle>Community feed unavailable</ItemTitle>
                <ItemBody>{feedErrorMessage}</ItemBody>
              </WallItemCard>
            )}
            {wallFeedEntries.map((item) => (
              <WallItemCard
                key={item.key}
                ref={(node) => {
                  if (item.postId) {
                    feedCardRefs.current[item.postId] = node;
                  }
                }}
                data-post-id={item.postId ?? undefined}
                $clickable={Boolean(item.postId)}
                onClick={() => openPost(item.postId)}
                onKeyDown={(event) => handleFeedCardKeyDown(event, item.postId)}
                role={item.postId ? 'button' : undefined}
                tabIndex={item.postId ? 0 : undefined}
              >
                <ItemTopRow>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <FeedBadge $kind={item.kind}>{item.badge}</FeedBadge>
                    {item.isPinned ? <FeedBadge $kind="post">Pinned</FeedBadge> : null}
                  </div>
                  <ItemTimestamp>
                    {item.dateLabel}
                    {item.edited ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOriginalSnapshot(item.originalPost ?? null);
                        }}
                        style={{ marginLeft: '0.45rem', background: 'none', border: 0, color: 'inherit', fontWeight: 700, cursor: 'pointer' }}
                      >
                        Edited
                      </button>
                    ) : null}
                  </ItemTimestamp>
                </ItemTopRow>
                <ItemTitle>{item.title}</ItemTitle>
                <ItemAuthor>By {item.author}</ItemAuthor>
                <PostRichContent
                  text={item.body}
                  images={item.images}
                  videoUrl={item.video}
                  compact
                  imageAlt={item.title}
                  stopPropagation
                />
                <ItemFooter>
                  {item.kind !== 'occasion' && (
                    <>
                      <ItemStat>{item.likes ?? 0} likes</ItemStat>
                      <ItemStat>{item.comments ?? 0} comments</ItemStat>
                      {canViewSeenStatus && item.postId && item.seenCount !== null ? (
                        <ItemActionButton
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActiveSeenPostId(item.postId ?? null);
                          }}
                        >
                          Seen by {item.seenCount ?? 0}
                        </ItemActionButton>
                      ) : null}
                    </>
                  )}
                  <ItemActionRow>
                    {item.kind !== 'occasion' ? (
                      <>
                        <ItemActionButton
                          type="button"
                          $active={Boolean(item.postId && likedPostIds.includes(item.postId))}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleToggleLike(item.postId);
                          }}
                          disabled={toggleLike.isPending}
                        >
                          {item.postId && likedPostIds.includes(item.postId) ? 'Liked' : 'Like'}
                        </ItemActionButton>
                        <ItemActionButton
                          type="button"
                          $active={Boolean(item.postId && commentedPostIds.includes(item.postId))}
                          onClick={(event) => {
                            event.stopPropagation();
                            openPost(item.postId);
                          }}
                        >
                          Comment
                        </ItemActionButton>
                        {item.canEdit && item.sourcePost ? (
                          <ItemActionButton
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              beginEdit(item.sourcePost as DashboardListItem);
                            }}
                          >
                            Edit
                          </ItemActionButton>
                        ) : null}
                        {canPinWall && item.postId ? (
                          <ItemActionButton
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleTogglePinned(item.postId, item.isPinned);
                            }}
                            disabled={setPostPinned.isPending}
                          >
                            {item.isPinned ? 'Unpin' : 'Pin'}
                          </ItemActionButton>
                        ) : null}
                      </>
                    ) : (
                      <ItemStat>Upcoming gathering</ItemStat>
                    )}
                  </ItemActionRow>
                </ItemFooter>
              </WallItemCard>
            ))}
            {!postsLoading && !postsError && wallFeedEntries.length === 0 && (
              <WallItemCard>
                <ItemTitle>No activity yet</ItemTitle>
                <ItemBody>Wall posts, prayers, and testimonies will appear here.</ItemBody>
              </WallItemCard>
            )}
          </WallFeed>

          {!postsLoading && !postsError && totalPages > 1 ? (
            <PaginationBar>
              <PaginationStatus>
                Page {Math.min(activePage, totalPages)} of {totalPages}
              </PaginationStatus>
              <PaginationActions>
                <PaginationButton
                  type="button"
                  onClick={() =>
                    updateSearch({
                      page: activePage - 1 > 1 ? String(activePage - 1) : null,
                    })
                  }
                  disabled={activePage <= 1}
                >
                  Previous
                </PaginationButton>
                <PaginationButton
                  type="button"
                  onClick={() => updateSearch({ page: String(activePage + 1) })}
                  disabled={activePage >= totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationActions>
            </PaginationBar>
          ) : null}
        </Main>

        <OriginalPostDialog snapshot={originalSnapshot} onClose={() => setOriginalSnapshot(null)} />
        <SeenByDialog
          open={Boolean(activeSeenPostId)}
          title={activeSeenEntry ? `Seen by ${activeSeenEntry.seenCount ?? 0}` : 'Seen by'}
          items={seenList ?? []}
          loading={seenListLoading}
          onClose={() => setActiveSeenPostId(null)}
        />

        <RightRail>
          <RailCard>
            <RailLabel>Quick Actions</RailLabel>
            <RailList>
              {sidebarQuickActions.map((action) => (
                <RailActionButton key={action.path} type="button" onClick={() => navigate(action.path)}>
                  {action.label}
                </RailActionButton>
              ))}
            </RailList>
          </RailCard>

          <RailCard>
            <RailLabel>Upcoming Events</RailLabel>
            <RailList>
              {upcomingEvents.map((event) => (
                <EventItem key={event.id || event.title}>
                  <EventTitle>{event.title || 'Community Event'}</EventTitle>
                  <EventMeta>
                    {formatEventMeta(event.start_date, event.start_time, event.location)}
                  </EventMeta>
                </EventItem>
              ))}
              {upcomingEvents.length === 0 && (
                <EventItem>
                  <EventTitle>No upcoming events</EventTitle>
                  <EventMeta>Check back soon for the next scheduled gathering.</EventMeta>
                </EventItem>
              )}
            </RailList>
          </RailCard>
        </RightRail>
      </Shell>
    </Page>
  );
};

export default DashboardHome;
